/**
 * 高德地图容器组件
 *
 * 功能：
 * 1. 初始化高德地图
 * 2. 根据所有设备遥测数据渲染四传感器目标 Marker
 * 3. 绘制各设备视场角范围
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { TelemetryData, DetectTarget, SoundTarget, useUIStore } from '@/store/useDeviceStore'
import { loadAMap } from '@/utils/loadAMap'

interface MapContainerProps {
  telemetryMap: Record<string, TelemetryData>  // 所有设备的遥测数据
  onlineDeviceIds: string[]
}

const FOLLOWED_DEVICE_IDS = new Set(['RK3588_001', 'RK3588_002', 'RK3588_003'])
const MAP_RECENTER_DISTANCE_METERS = 100

function calculateDistanceMeters([lon1, lat1]: [number, number], [lon2, lat2]: [number, number]) {
  const toRadians = (degrees: number) => degrees * Math.PI / 180
  const earthRadiusMeters = 6371000
  const latitudeDelta = toRadians(lat2 - lat1)
  const longitudeDelta = toRadians(lon2 - lon1)
  const a = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(longitudeDelta / 2) ** 2

  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// 传感器类型对应的图标颜色
const SENSOR_COLORS = {
  vis: '#52c41a', // 绿色 - 可见光
  ir: '#ff4d4f',  // 红色 - 红外
  snd: '#faad14', // 黄色 - 声学
  rad: '#1890ff', // 蓝色 - 无线电
}

function MapContainer({ telemetryMap, onlineDeviceIds }: MapContainerProps) {
  const mapRef = useRef<AMap.Map | null>(null)
  const markersRef = useRef<AMap.Marker[]>([])
  const fovPolygonsRef = useRef<AMap.Polygon[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const followedCenterRef = useRef<[number, number] | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [markerUpdateKey, setMarkerUpdateKey] = useState(0)

  // 从全局状态获取地图中心配置
  const mapCenter = useUIStore((state) => state.mapCenter)

  /**
   * 防抖更新Marker(避免频繁渲染)
   */
  useEffect(() => {
    const telemetryValues = Object.values(telemetryMap)
    if (telemetryValues.length === 0) return

    const timer = setTimeout(() => {
      setMarkerUpdateKey(prev => prev + 1)
    }, 300) // 300ms防抖

    return () => clearTimeout(timer)
  }, [telemetryMap])

  /**
   * 动态加载并初始化地图
   */
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    let mounted = true

    loadAMap()
      .then((AMapInstance) => {
        if (!mounted || !containerRef.current) return

        // 创建地图实例，使用全局配置的中心点
        const map = new AMapInstance.Map(containerRef.current, {
          zoom: mapCenter.zoom,
          center: [mapCenter.lon, mapCenter.lat],
          viewMode: '2D',
          mapStyle: 'amap://styles/normal',
          resizeEnable: true,      // 允许自适应容器大小
          zooms: [3, 20],          // 缩放级别范围
          dragEnable: true,        // 允许拖拽
          zoomEnable: true,        // 允许缩放
          doubleClickZoom: true,   // 双击缩放
          keyboardEnable: true,    // 键盘控制
        })

        mapRef.current = map

        // 加载并添加比例尺控件
        AMapInstance.plugin('AMap.Scale', () => {
          const scale = new AMapInstance.Scale({
            position: 'LB', // 左下角(logo隐藏后此位置空闲)
          })
          map.addControl(scale)
        })

        // 加载并添加缩放工具栏
        AMapInstance.plugin('AMap.ToolBar', () => {
          const toolbar = new AMapInstance.ToolBar({
            position: 'RT', // 右上角
          })
          map.addControl(toolbar)
        })

        setMapLoaded(true)
      })
      .catch((err) => {
        if (mounted) {
          console.error('高德地图加载失败:', err)
          setLoadError(err.message || '地图加载失败')
        }
      })

    // 清理
    return () => {
      mounted = false
      if (mapRef.current) {
        mapRef.current.destroy()
        mapRef.current = null
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // 注意：初始化只执行一次，mapCenter 变化由下面的 useEffect 处理

  /**
   * 响应地图中心配置变化
   */
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    // 平滑移动到新中心
    mapRef.current.setZoomAndCenter(mapCenter.zoom, [mapCenter.lon, mapCenter.lat], false, 500)
  }, [mapCenter, mapLoaded])

  /**
   * 按在线设备的吊舱坐标跟随地图中心。
   * 一个设备时直接跟随；两个设备时为连线中点；三个及以上时为几何中心。
   */
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    const onlineIds = new Set(onlineDeviceIds)
    const positions = Object.values(telemetryMap)
      .filter((telemetry) => FOLLOWED_DEVICE_IDS.has(telemetry.dev_id) && onlineIds.has(telemetry.dev_id))
      .map((telemetry) => telemetry.status?.pot_info)
      .filter((potInfo): potInfo is NonNullable<typeof potInfo> => {
        if (!potInfo) return false
        return Number.isFinite(potInfo.pot_lat) && Number.isFinite(potInfo.pot_lon) &&
          (potInfo.pot_lat !== 0 || potInfo.pot_lon !== 0)
      })

    if (positions.length === 0) {
      followedCenterRef.current = null
      return
    }

    const center = positions.reduce(
      (acc, position) => ({
        lon: acc.lon + position.pot_lon,
        lat: acc.lat + position.pot_lat,
      }),
      { lon: 0, lat: 0 }
    )

    const nextCenter: [number, number] = [center.lon / positions.length, center.lat / positions.length]
    const previousCenter = followedCenterRef.current
    if (previousCenter && calculateDistanceMeters(previousCenter, nextCenter) < MAP_RECENTER_DISTANCE_METERS) {
      return
    }

    followedCenterRef.current = nextCenter
    mapRef.current.setCenter(nextCenter)
  }, [telemetryMap, onlineDeviceIds, mapLoaded])

  /**
   * 创建目标 Marker
   */
  const createMarker = useCallback(
    (
      lon: number,
      lat: number,
      sensorType: keyof typeof SENSOR_COLORS,
      id: number,
      conf: number
    ) => {
      const color = SENSOR_COLORS[sensorType]

      // 创建自定义图标
      const icon = new AMap.Icon({
        size: new AMap.Size(24, 24),
        image: `data:image/svg+xml,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="${color}" opacity="0.8"/>
            <circle cx="12" cy="12" r="5" fill="white"/>
          </svg>
        `)}`,
        imageSize: new AMap.Size(24, 24),
      })

      const marker = new AMap.Marker({
        position: [lon, lat],
        icon,
        anchor: 'center',
        label: {
          content: `<div style="
            background: ${color};
            color: white;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 10px;
            white-space: nowrap;
          ">
            ${sensorType.toUpperCase()} #${id} (${conf}%)
          </div>`,
          offset: new AMap.Pixel(0, -20),
        },
      })

      return marker
    },
    []
  )

  /**
   * 更新 Markers（遍历所有设备的遥测数据）
   */
  useEffect(() => {
    if (!mapRef.current) return

    const map = mapRef.current
    const telemetryValues = Object.values(telemetryMap)

    // 清除旧 Markers
    if (markersRef.current.length > 0) {
      map.remove(markersRef.current)
      markersRef.current = []
    }

    if (telemetryValues.length === 0) return

    const newMarkers: AMap.Marker[] = []

    // 遍历所有设备的遥测数据
    for (const telemetry of telemetryValues) {
      const { detect } = telemetry

      // 可见光目标
      detect.vis?.forEach((target: DetectTarget) => {
        if (target.geo) {
          const marker = createMarker(
            target.geo.lon,
            target.geo.lat,
            'vis',
            target.id,
            target.conf
          )
          newMarkers.push(marker)
        }
      })

      // 红外目标
      detect.ir?.forEach((target: DetectTarget) => {
        if (target.geo) {
          const marker = createMarker(
            target.geo.lon,
            target.geo.lat,
            'ir',
            target.id,
            target.conf
          )
          newMarkers.push(marker)
        }
      })

      // 声学目标
      detect.snd?.forEach((target: SoundTarget) => {
        if (target.geo) {
          const marker = createMarker(
            target.geo.lon,
            target.geo.lat,
            'snd',
            0,
            target.conf
          )
          newMarkers.push(marker)
        }
      })

      // 吊舱位置标注
      if (telemetry.status?.pot_info) {
        const {
          pot_lat = 0,
          pot_lon = 0,
          pot_alti = 0,
          laser_dis = 0,
          servo_az = 0,
          servo_el = 0
        } = telemetry.status.pot_info

        // 只有当位置有效时才显示吊舱标注
        if (pot_lat !== 0 || pot_lon !== 0) {
          const podIcon = new AMap.Icon({
            size: new AMap.Size(32, 32),
            image: `data:image/svg+xml,${encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                <path d="M16 4 L28 24 L16 20 L4 24 Z" fill="#fa8c16" stroke="#fff" stroke-width="2" opacity="0.9"/>
                <circle cx="16" cy="16" r="3" fill="white"/>
              </svg>
            `)}`,
            imageSize: new AMap.Size(32, 32),
          })

          const podMarker = new AMap.Marker({
            position: [pot_lon, pot_lat],
            icon: podIcon,
            anchor: 'center',
            title: `${telemetry.dev_id} 吊舱 - 海拔: ${pot_alti.toFixed(0)}m`,
            zIndex: 100,
            label: {
              content: `<div style="
                background: #fa8c16;
                color: white;
                padding: 3px 8px;
                border-radius: 4px;
                font-size: 11px;
                white-space: nowrap;
                font-weight: bold;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              ">
                ${telemetry.dev_id} | 海拔${pot_alti.toFixed(0)}m
              </div>`,
              offset: new AMap.Pixel(0, -25),
            },
          })

          // 添加信息窗口
          const infoWindow = new AMap.InfoWindow({
            isCustom: false,
            content: `
              <div style="padding: 10px; font-size: 12px;">
                <strong style="font-size: 14px; color: #fa8c16;">${telemetry.dev_id} 吊舱位置</strong><br/>
                <div style="margin-top: 8px; line-height: 1.6;">
                  <div>GPS: ${pot_lat.toFixed(6)}, ${pot_lon.toFixed(6)}</div>
                  <div>海拔: ${pot_alti.toFixed(0)}m</div>
                  <div>方位: ${servo_az.toFixed(1)}° / 俯仰: ${servo_el.toFixed(1)}°</div>
                  <div>激光测距: ${laser_dis.toFixed(0)}m</div>
                </div>
              </div>
            `,
            offset: new AMap.Pixel(0, -35),
          })

          podMarker.on('click', () => {
            infoWindow.open(map, [pot_lon, pot_lat])
          })

          newMarkers.push(podMarker)
        }
      }

      // 无线电目标 (通常没有地理坐标)
      // detect.rad 处理略
    }

    // 添加新 Markers
    if (newMarkers.length > 0) {
      map.add(newMarkers)
      markersRef.current = newMarkers
    }
  }, [markerUpdateKey, telemetryMap, createMarker])

  /**
   * 更新所有设备的视场角多边形
   */
  useEffect(() => {
    if (!mapRef.current) return

    const map = mapRef.current

    // 清除旧多边形
    if (fovPolygonsRef.current.length > 0) {
      map.remove(fovPolygonsRef.current)
      fovPolygonsRef.current = []
    }

    const newPolygons: AMap.Polygon[] = []

    for (const telemetry of Object.values(telemetryMap)) {
      const fov = telemetry?.status?.fov
      if (!fov || fov.length !== 4) continue

      const path: [number, number][] = fov.map(
        (point: [number, number]) => [point[0], point[1]]
      )

      const polygon = new AMap.Polygon({
        path,
        strokeColor: '#1890ff',
        strokeWeight: 2,
        strokeOpacity: 0.8,
        fillColor: '#1890ff',
        fillOpacity: 0.2,
      })

      newPolygons.push(polygon)
    }

    if (newPolygons.length > 0) {
      map.add(newPolygons)
      fovPolygonsRef.current = newPolygons
    }
  }, [telemetryMap])

  return (
    <div
      ref={containerRef}
      className="map-container"
      style={{ width: '100%', height: '100%' }}
    >
      {/* 加载中或错误提示 */}
      {!mapLoaded && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            background: '#f5f5f5',
            color: loadError ? '#ff4d4f' : '#999',
          }}
        >
          {loadError || '地图加载中...'}
        </div>
      )}
    </div>
  )
}

export default MapContainer
